/**
 * The switch for the provenance overlay, and its key.
 *
 * Sits in the corner of every screen and is deliberately plain — it is a tool
 * for checking the data model against the screens, not part of the app. It is
 * hidden entirely until the overlay is turned on with `?trace=1` or
 * `Ctrl` + `Alt` + `T`, so a child never sees it.
 */
import { useState } from 'react'
import { GROUPS, TABLES, openDataMap, useTrace } from '../lib/trace'
import type { GroupKey } from '../lib/trace'

export function TracePanel() {
  const { on, toggle } = useTrace()
  // Open the first time, so the key is the first thing seen, not a riddle.
  const [openKey, setOpenKey] = useState(true)

  if (!on) return null

  const byGroup = Object.entries(TABLES).reduce<Record<string, string[]>>(
    (acc, [tid, entry]) => {
      (acc[entry.group] ||= []).push(`${tid} ${entry.table}`)
      return acc
    },
    {},
  )

  return (
    <div style={{
      position: 'fixed', right: 12, bottom: 12, zIndex: 60,
      fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 11,
      maxWidth: 300,
    }}>
      {openKey ? (
        <div style={{
          background: '#fff', border: '1px solid #D8DFE7', borderRadius: 8,
          padding: '10px 12px', marginBottom: 8, lineHeight: 1.6,
          boxShadow: '0 6px 20px rgba(21,32,43,0.16)',
          maxHeight: '60vh', overflowY: 'auto',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            Where each value comes from
          </div>
          <p style={{
            margin: '0 0 10px', fontFamily: 'inherit', fontSize: 10,
            color: '#55636F', lineHeight: 1.5,
          }}>
            Each label names a table and a column — <b>T8.successes</b> is the
            <b> successes</b> column of table <b>T8</b>.
            <br /><br />
            <b>Dashed box, filled label</b>: shown here, READ from that table.
            <br />
            <b>Solid box, → arrow</b>: what you type or tap here is SAVED to that
            table.
            <br />
            <b>calc</b>: worked out on the page, in no table.
            <span style={{ color: '#B02A2A' }}> Red</span>: the label itself is wrong.
            <br /><br />
            <b>Click any label</b> to open the data map at that table.
          </p>
          {(Object.keys(GROUPS) as GroupKey[]).map(g => (
            <div key={g} style={{ marginBottom: 8 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontWeight: 700, color: GROUPS[g].colour, marginBottom: 2,
              }}>
                <span style={{
                  width: 9, height: 9, borderRadius: 2,
                  background: GROUPS[g].colour, display: 'inline-block',
                }} />
                {GROUPS[g].label}
              </div>
              <div style={{ color: '#55636F', fontSize: 10, paddingLeft: 15 }}>
                {(byGroup[g] || []).join(' · ')}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => setOpenKey(v => !v)}
          style={buttonStyle('#31404E')}
        >
          {openKey ? 'Hide key' : 'Key'}
        </button>
        <button type="button" onClick={() => openDataMap('T1')} style={buttonStyle('#2563EB')}>
          Data map
        </button>
        <button type="button" onClick={toggle} style={buttonStyle('#B02A2A')}>
          Labels off
        </button>
      </div>
    </div>
  )
}

function buttonStyle(background: string) {
  return {
    fontFamily: 'inherit', fontSize: 11, fontWeight: 700,
    color: '#fff', background, border: 'none', borderRadius: 6,
    padding: '6px 10px', cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(21,32,43,0.2)',
  } as const
}
